import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";

type FollowsApi = Pick<ApiClient, "get" | "put" | "delete">;

interface FollowsAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
    readonly isSignedIn: boolean;
}

/** The podcasts a given podcast can be followed as: what the account list needs to show it */
export type FollowTarget = Pick<Podcast, "id" | "name" | "slug" | "art" | "blurhash">;

/**
 * The podcasts the signed-in listener follows, kept on the account (`/me/follows`).
 * Following needs an account; signed out, the follow buttons ask to sign in instead.
 * Changes show at once and are undone if the API refuses them.
 */
export class FollowsStore {
    podcasts: FollowedPodcast[] = [];
    /** True once the list of the signed-in user has been fetched */
    loaded = false;

    private generation = 0;

    constructor(
        private readonly client: FollowsApi,
        private readonly auth: FollowsAuth
    ) {
        makeAutoObservable<FollowsStore, "client" | "auth" | "generation">(this, {
            client: false,
            auth: false,
            generation: false,
        });

        reaction(
            () => this.auth.status,
            (status) => {
                this.generation++;
                this.podcasts = [];
                this.loaded = false;
                if (status === "authenticated") {
                    void this.load();
                }
            },
            { fireImmediately: true }
        );
    }

    isFollowing(podcastId: number): boolean {
        return this.podcasts.some((podcast) => podcast.id === podcastId);
    }

    async load() {
        if (!this.auth.isSignedIn) return;

        const generation = this.generation;
        try {
            const response = await this.client.get<FollowsResponse>("/me/follows");
            runInAction(() => {
                // Signed out, or in as someone else, while the request was on its way
                if (generation !== this.generation) return;
                this.podcasts = response.podcasts;
                this.loaded = true;
            });
        } catch (error) {
            console.warn("Could not load followed podcasts", error);
        }
    }

    /** @returns false when the follow could not be saved */
    async follow(podcast: FollowTarget): Promise<boolean> {
        if (!this.auth.isSignedIn) return false;
        if (this.isFollowing(podcast.id)) return true;

        const generation = this.generation;
        this.podcasts.unshift({
            id: podcast.id,
            name: podcast.name,
            slug: podcast.slug,
            art: podcast.art ?? null,
            blurhash: podcast.blurhash,
            followed_at: new Date().toISOString(),
        });
        try {
            await this.client.put(`/me/follows/${podcast.id}`);
            return true;
        } catch (error) {
            console.warn("Could not follow podcast", error);
            runInAction(() => {
                if (generation === this.generation) {
                    this.podcasts = this.podcasts.filter((p) => p.id !== podcast.id);
                }
            });
            return false;
        }
    }

    /** @returns false when the unfollow could not be saved */
    async unfollow(podcastId: number): Promise<boolean> {
        if (!this.auth.isSignedIn) return false;

        const generation = this.generation;
        const before = this.podcasts;
        this.podcasts = before.filter((p) => p.id !== podcastId);
        try {
            await this.client.delete(`/me/follows/${podcastId}`);
            return true;
        } catch (error) {
            console.warn("Could not unfollow podcast", error);
            runInAction(() => {
                if (generation === this.generation) {
                    this.podcasts = before;
                }
            });
            return false;
        }
    }
}
